/**
 * 스킬 창 (K). 1~4차 칸, SP 분배, 퀵슬롯 등록.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import { ADVANCEMENT } from '../data/balance';
import { getJob, jobOnLineAtRank, type JobRank } from '../data/jobs';
import { getSkillEntry } from '../data/skills';
import { EV } from '../game/events';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const WIDTH = 440;
const HEIGHT = 500;
const PAD = 14;
const LIST_TOP = 92;
const LIST_BOTTOM = 400;
const ROW_H = 44;
const VISIBLE_ROWS = Math.floor((LIST_BOTTOM - LIST_TOP) / ROW_H);
const SLOT_KEYS = ['Shift', 'A', 'F'] as const;
const RANK_TABS: { rank: JobRank; label: string }[] = [
  { rank: 1, label: '1차' },
  { rank: 2, label: '2차' },
  { rank: 3, label: '3차' },
  { rank: 4, label: '4차' },
];

function lockMessage(rank: JobRank): string {
  if (rank <= 1) return '';
  const lv = rank === 2 ? ADVANCEMENT.rank2 : rank === 3 ? ADVANCEMENT.rank3 : ADVANCEMENT.rank4;
  return `${lv}레벨 전직 후 개방`;
}

export class SkillWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly listHost: Phaser.GameObjects.Container;
  private readonly rows: Phaser.GameObjects.Container[] = [];
  private readonly tabLabels: Phaser.GameObjects.Text[] = [];
  private readonly spText: Phaser.GameObjects.Text;
  private readonly hintText: Phaser.GameObjects.Text;
  private readonly lockText: Phaser.GameObjects.Text;
  private selectedId: string | null = null;
  private tab: JobRank = 1;
  private scroll = 0;

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const x = scene.scale.width / 2 - WIDTH / 2;
    const y = 48;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(30);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    const title = scene.add.text(PAD, 10, '스킬', {
      fontFamily: UI_FONT,
      fontSize: '16px',
      color: TEXT_COLOR.dark,
    });
    const close = scene.add
      .text(WIDTH - PAD, 12, '✕', {
        fontFamily: UI_MONO,
        fontSize: '14px',
        color: TEXT_COLOR.dark,
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => this.close());
    this.spText = scene.add.text(PAD, 32, '', {
      fontFamily: UI_FONT,
      fontSize: '13px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.container.add([title, close, this.spText]);

    RANK_TABS.forEach((tab, i) => {
      const btn = scene.add
        .text(PAD + i * 102, 56, tab.label, {
          fontFamily: UI_FONT,
          fontSize: '13px',
          color: TEXT_COLOR.dark,
          backgroundColor: '#d8c8a8',
          padding: { x: 14, y: 4 },
        })
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.tab = tab.rank;
        this.selectedId = null;
        this.scroll = 0;
        Sfx.uiClick();
        this.refresh();
      });
      this.tabLabels.push(btn);
      this.container.add(btn);
    });

    this.listHost = scene.add.container(0, 0);
    this.container.add(this.listHost);
    const maskG = scene.add.graphics();
    maskG.fillStyle(0xffffff, 1);
    maskG.fillRect(x + 8, y + LIST_TOP, WIDTH - 16, LIST_BOTTOM - LIST_TOP);
    maskG.setVisible(false);
    this.listHost.setMask(maskG.createGeometryMask());

    this.lockText = scene.add.text(PAD, LIST_TOP + 48, '', {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2 },
    });
    this.container.add(this.lockText);

    this.hintText = scene.add.text(PAD, HEIGHT - 78, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2 },
    });
    this.container.add(this.hintText);

    SLOT_KEYS.forEach((key, i) => {
      const btn = scene.add
        .text(PAD + i * 128, HEIGHT - 36, `${key}`, {
          fontFamily: UI_MONO,
          fontSize: '12px',
          color: TEXT_COLOR.dark,
          backgroundColor: '#d8c8a8',
          padding: { x: 10, y: 4 },
        })
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        if (!this.selectedId) return;
        if (this.rankLocked()) {
          Sfx.deny();
          return;
        }
        if (this.state.bindQuickSlot(i, this.selectedId)) Sfx.uiClick();
        else Sfx.deny();
        this.refresh();
      });
      this.container.add(btn);
    });

    scene.input.on('wheel', (_p: unknown, _over: unknown, _dx: number, dy: number) => {
      if (!this.visible || this.rankLocked()) return;
      const ids = this.tabSkillIds();
      const maxScroll = Math.max(0, ids.length - VISIBLE_ROWS);
      this.scroll = Phaser.Math.Clamp(this.scroll + (dy > 0 ? 1 : -1), 0, maxScroll);
      this.refresh();
    });

    this.state.events.on(EV.statsChanged, this.refresh, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.state.events.off(EV.statsChanged, this.refresh, this);
    });
    this.refresh();
  }

  toggle(): boolean {
    this.container.setVisible(!this.container.visible);
    if (this.container.visible) {
      this.tab = getJob(this.state.jobId).rank;
      this.scroll = 0;
      this.refresh();
    }
    return this.container.visible;
  }

  close(): void {
    this.container.setVisible(false);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  private rankLocked(): boolean {
    return getJob(this.state.jobId).rank < this.tab;
  }

  private tabSkillIds(): string[] {
    const job = jobOnLineAtRank(this.state.jobId, this.tab);
    return job?.skillIds ?? [];
  }

  private refresh = (): void => {
    const current = getJob(this.state.jobId);
    this.spText.setText(`${current.name}  ·  남은 SP ${this.state.sp}`);
    this.tabLabels.forEach((label, i) => {
      const rank = RANK_TABS[i]?.rank ?? 1;
      const on = this.tab === rank;
      label.setBackgroundColor(on ? '#c4a574' : '#d8c8a8');
    });

    for (const row of this.rows) row.destroy();
    this.rows.length = 0;

    const locked = this.rankLocked();
    this.lockText.setVisible(locked);
    this.lockText.setText(locked ? lockMessage(this.tab) : '');

    if (!locked) {
      const ids = this.tabSkillIds();
      const maxScroll = Math.max(0, ids.length - VISIBLE_ROWS);
      this.scroll = Phaser.Math.Clamp(this.scroll, 0, maxScroll);
      ids.forEach((id, i) => {
        if (i < this.scroll || i >= this.scroll + VISIBLE_ROWS) return;
        const entry = getSkillEntry(id);
        if (!entry) return;
        const lv = this.state.skillLevel(id);
        const y = LIST_TOP + (i - this.scroll) * ROW_H;
        const row = this.listHost.scene.add.container(0, y);
        const hit = this.listHost.scene.add
          .rectangle(WIDTH / 2, 20, WIDTH - 20, ROW_H - 4, this.selectedId === id ? 0xc8b890 : 0xeee2c4, 1)
          .setInteractive({ useHandCursor: true });
        hit.on('pointerdown', () => {
          this.selectedId = id;
          this.refresh();
        });
        const icon = this.listHost.scene.add.image(PAD + 16, 20, entry.icon).setOrigin(0.5).setDisplaySize(26, 26);
        const name = this.listHost.scene.add.text(PAD + 36, 6, `${entry.name}  Lv.${lv}/${entry.maxLevel}`, {
          fontFamily: UI_FONT,
          fontSize: '13px',
          color: TEXT_COLOR.dark,
          wordWrap: { width: WIDTH - 88, useAdvancedWrap: true },
        });
        const plus = this.listHost.scene.add
          .text(WIDTH - 36, 20, '+', {
            fontFamily: UI_MONO,
            fontSize: '20px',
            color: this.state.sp > 0 && lv < entry.maxLevel ? TEXT_COLOR.dark : '#8a8a8a',
          })
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true });
        plus.on('pointerdown', () => {
          if (this.state.allocateSp(id)) Sfx.uiClick();
          else Sfx.deny();
        });
        row.add([hit, icon, name, plus]);
        this.listHost.add(row);
        this.rows.push(row);
      });
    }

    const selected = !locked && this.selectedId ? getSkillEntry(this.selectedId) : undefined;
    const bound = this.state.quickSlots
      .map((id, i) => `${SLOT_KEYS[i]}:${getSkillEntry(id)?.name ?? '-'}`)
      .join('  ');
    this.hintText.setText(
      selected
        ? `${selected.description}\n${bound}`
        : `칸에서 스킬을 고른 뒤 Shift/A/F 에 등록합니다.\n${bound}`,
    );
  };
}
